import { useState } from "react";
import { ChevronsUp } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { api } from "../../lib/api";
import { toast } from "sonner";

const RULESET_LABELS = { pf2e: "Pathfinder 2e", dnd5e: "D&D 5e", homebrew: "Homebrew" };

function RankRow({ ruleset, data, isSelf, onRenamed }) {
  const [saving, setSaving] = useState(false);
  const pct = data.next_rank ? Math.max(0, Math.min(100, Math.round((data.xp / data.next_rank.xp) * 100))) : 100;

  const chooseName = async (name) => {
    setSaving(true);
    try {
      await api.patch("/gauntlets/rank-name", { rank_key: data.rank_key, chosen_name: name });
      onRenamed(ruleset, name);
    } catch {
      toast.error("Couldn't update that title");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-1" data-testid={`rank-row-${ruleset}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="text-[#f1e0c5]/50 uppercase tracking-wider text-[10px]">{RULESET_LABELS[ruleset]}</span>
        <span className="text-[#f1e0c5]/40">{data.xp} XP{data.next_rank ? ` · ${data.next_rank.xp - data.xp} to next rank` : " · Max Rank"}</span>
      </div>
      <div className="flex items-center gap-2">
        {isSelf && data.rank_options.length > 1 ? (
          <Select value={data.rank_name} onValueChange={chooseName} disabled={saving}>
            <SelectTrigger data-testid={`rank-name-select-${ruleset}`} className="bg-black/30 border-white/10 h-8 text-sm font-display w-fit gap-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              {data.rank_options.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <span className="font-display text-base text-[#3B82F6]" data-testid={`rank-name-${ruleset}`}>{data.rank_name}</span>
        )}
      </div>
      <div className="h-1.5 rounded-full bg-black/40 overflow-hidden">
        <div className="h-full rounded-full bg-[#3B82F6] transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// Rank titles per ruleset — total XP is the SAME points earned from completed raids (see
// gauntlet_engine.py), just tallied per ruleset instead of per raid_type. Shown next to a
// Knight's avatar so their standing is visible at a glance.
export function RankLadderPanel({ ranks, isSelf, onUpdate }) {
  const handleRename = (ruleset, name) => {
    onUpdate({ ...ranks, [ruleset]: { ...ranks[ruleset], rank_name: name } });
  };
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-5 space-y-4" data-testid="rank-ladder-panel">
      <h3 className="font-display text-lg flex items-center gap-2"><ChevronsUp className="w-4 h-4 text-[#3B82F6]" /> Rank</h3>
      <div className="grid sm:grid-cols-3 gap-4">
        {Object.entries(ranks).map(([ruleset, data]) => (
          <RankRow key={ruleset} ruleset={ruleset} data={data} isSelf={isSelf} onRenamed={handleRename} />
        ))}
      </div>
    </div>
  );
}
